import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Star, Eye, MessageCircle, ShieldCheck, Share2, ImageOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PropertyListing, getWhatsAppLink } from "@/data/sample-listings";
import { trackWhatsAppClick, trackTourView } from "@/lib/analytics";
import { incrementWhatsAppClick, incrementTourView, getTourShareUrl } from "@/lib/listings";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { formatPrice, getPriceLabel } from "@/lib/pricing";

interface ListingCardProps {
  listing: PropertyListing;
  index?: number;
  compact?: boolean;
}

const categoryLabels: Record<string, string> = {
  'event-centre': 'Event Centre',
  'hotel': 'Hotel',
  'apartment': 'Apartment',
  'office': 'Office',
  'restaurant': 'Restaurant',
};

export const ListingCard = ({ listing, index = 0, compact = false }: ListingCardProps) => {
  const { toast } = useToast();
  const [imageError, setImageError] = useState(false);
  const displayPrice = formatPrice(listing.price, listing.priceText);
  const displayPriceLabel = getPriceLabel(listing.priceType, listing.rentFrequency);
  const whatsappMessage = `Hello! I'm interested in viewing "${listing.title}" in ${listing.location}. Can I get more information and schedule a visit?`;

  const handleWhatsAppClick = () => {
    trackWhatsAppClick(listing.id, listing.category);
    incrementWhatsAppClick(listing.id).catch(() => {});
  };

  const handleTourClick = () => {
    trackTourView(listing.id, listing.title);
    incrementTourView(listing.id).catch(() => {});
  };

  const handleShare = () => {
    const url = getTourShareUrl(listing.slug);
    navigator.clipboard.writeText(url)
      .then(() => toast({ title: "Tour link copied!", description: "Share it on WhatsApp for a branded preview." }))
      .catch(() => toast({ title: "Link copied!", description: url }));
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="group bg-card rounded-2xl overflow-hidden shadow-md card-hover border border-border"
    >
      {/* Image Container */}
      <div className={`relative overflow-hidden bg-muted ${compact ? "aspect-square" : "aspect-[4/3]"}`}>
        {!imageError && listing.imageUrl ? (
          <img
            src={listing.imageUrl}
            alt={listing.title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
            loading="lazy"
            fetchPriority="low"
            onError={() => setImageError(true)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-muted">
            <div className="text-center">
              <ImageOff className="w-12 h-12 text-muted-foreground mx-auto mb-2" />
              <p className="text-xs text-muted-foreground">Image unavailable</p>
            </div>
          </div>
        )}
        
        {/* Overlay Gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-foreground/80 via-foreground/20 to-transparent" />

        {/* Category Badge */}
        <div className={`absolute ${compact ? "top-2 left-2" : "top-4 left-4"}`}>
          <span className={`rounded-full bg-background/90 backdrop-blur-sm font-medium text-foreground ${compact ? "px-2 py-1 text-[10px]" : "px-3 py-1.5 text-sm"}`}>
            {categoryLabels[listing.category]}
          </span>
        </div>

        {/* Featured Badge */}
        {listing.featured && (
          <div className={`absolute ${compact ? "top-2 right-2" : "top-4 right-4"}`}>
            <span className="badge-360">Featured</span>
          </div>
        )}

        {/* Verified Badge */}
        {listing.verified && !listing.featured && (
          <div className={`absolute ${compact ? "top-2 right-2" : "top-4 right-4"}`}>
            <span className={`flex items-center gap-1 rounded-full bg-primary/90 backdrop-blur-sm text-primary-foreground font-semibold ${compact ? "px-2 py-1 text-[10px]" : "px-2.5 py-1 text-xs"}`}>
              <ShieldCheck className="w-3.5 h-3.5" />
              {compact ? "V" : "Verified"}
            </span>
          </div>
        )}

        {/* Quick Actions on Hover */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <Link to={`/tour/${listing.slug}`} onClick={handleTourClick}>
            <Button size={compact ? "sm" : "lg"} className="gold-gradient text-foreground font-semibold shadow-gold">
              <Eye className={`${compact ? "w-4 h-4 mr-1.5" : "w-5 h-5 mr-2"}`} />
              {compact ? "Open" : "View 360° Tour"}
            </Button>
          </Link>
        </div>

        {/* Bottom Info */}
        <div className={`absolute ${compact ? "bottom-2 left-2 right-2" : "bottom-4 left-4 right-4"}`}>
          <div className="flex items-center justify-between text-background">
            <div className="flex items-center gap-1 text-xs sm:text-sm">
              <Star className="w-3.5 h-3.5 fill-secondary text-secondary" />
              <span className="font-semibold">{listing.rating}</span>
              {!compact && <span className="text-background/70">({listing.reviewCount})</span>}
            </div>
            <div className="flex items-center gap-1 text-xs sm:text-sm">
              <MapPin className="w-3.5 h-3.5" />
              <span>{listing.city.charAt(0).toUpperCase() + listing.city.slice(1)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className={compact ? "p-3" : "p-5"}>
        <h3 className={`font-display font-bold text-foreground mb-1 group-hover:text-primary transition-colors ${compact ? "text-sm leading-tight line-clamp-2" : "text-lg"}`}>
          {listing.title}
        </h3>
        <p className={`text-muted-foreground flex items-center gap-1 ${compact ? "text-xs mb-2 line-clamp-1" : "text-sm mb-3"}`}>
          <MapPin className="w-3.5 h-3.5" />
          {listing.location}
        </p>

        {/* Amenities Preview */}
        {!compact && <div className="flex flex-wrap gap-1.5 mb-4">
          {listing.amenities.slice(0, 3).map((amenity) => (
            <span 
              key={amenity}
              className="px-2 py-1 rounded-md bg-muted text-xs font-medium text-muted-foreground"
            >
              {amenity}
            </span>
          ))}
          {listing.amenities.length > 3 && (
            <span className="px-2 py-1 rounded-md bg-muted text-xs font-medium text-primary">
              +{listing.amenities.length - 3} more
            </span>
          )}
        </div>}

        {/* Architect Credit */}
        {listing.architectFirm && !compact && (
          <div className="mb-4">
            <Link
              to={`/tours?search=${encodeURIComponent(listing.architectFirm)}`}
              className="architect-badge text-xs"
            >
              Architecture by {listing.architectFirm}
            </Link>
          </div>
        )}

        {/* Price & CTA */}
        <div className={`${compact ? "pt-2 border-t border-border" : "pt-4 border-t border-border"}`}>
          <div className={`flex items-center justify-between ${compact ? "mb-2" : "mb-3"}`}>
            <div>
              <p className={`font-display font-bold text-primary ${compact ? "text-sm" : "text-xl"}`}>{displayPrice}</p>
              {!compact && <p className="text-xs text-muted-foreground">{displayPriceLabel}</p>}
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={handleShare}
              className={`gap-1.5 text-muted-foreground hover:text-foreground ${compact ? "px-2" : ""}`}
              data-testid={`button-share-${listing.id}`}
            >
              <Share2 className="w-4 h-4" />
              {!compact && "Share"}
            </Button>
          </div>
          <div className="flex gap-2">
            <a
              href={getWhatsAppLink(`Hello Vista360, I am interested in "${listing.title}" at ${listing.location}. I'd like to book an inspection.`)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleWhatsAppClick}
              className="flex-1"
            >
              <Button size="sm" className={`whatsapp-btn w-full ${compact ? "text-xs px-2" : "text-sm"}`}>
                <MessageCircle className="w-4 h-4" />
                {compact ? "WhatsApp" : "Book Inspection"}
              </Button>
            </a>
          </div>
        </div>
      </div>
    </motion.div>
  );
};
